/**
 * Formatting actions for the legal-document Markdown editor toolbar.
 *
 * Every action is a pure function of the text and the selection, and returns
 * an *edit* — replace `[start, end)` with `text`, then select
 * `[selectionStart, selectionEnd)` — so the editor can apply it through the
 * browser's own undo stack (see applyEditToTextarea).
 */

export const FORMAT_ACTIONS = {
  heading: { label: 'Section heading', short: 'Heading', shortcut: null },
  subheading: { label: 'Subheading', short: 'Subheading', shortcut: null },
  bold: { label: 'Bold', short: 'B', shortcut: 'b' },
  italic: { label: 'Italic', short: 'I', shortcut: 'i' },
  link: { label: 'Link', short: 'Link', shortcut: 'k' },
  bullets: { label: 'Bulleted list', short: '• List', shortcut: null },
  numbers: { label: 'Numbered list', short: '1. List', shortcut: null },
  callout: { label: 'Callout box', short: 'Callout', shortcut: null },
  counselNote: { label: 'Counsel note (hidden from the public page)', short: 'Counsel note', shortcut: null },
};

const INLINE_MARKERS = { bold: '**', italic: '*' };
const INLINE_PLACEHOLDERS = { bold: 'bold text', italic: 'italic text' };

const HEADING_PREFIX = /^#{1,3}\s+/;
const LINE_PREFIXES = {
  heading: '## ',
  subheading: '### ',
  bullets: '- ',
  callout: '> ',
};
const LINE_PATTERNS = {
  heading: /^##\s+/,
  subheading: /^###\s+/,
  bullets: /^[-*]\s+/,
  numbers: /^\d+\.\s+/,
  callout: /^>\s?/,
};

const edit = (start, end, text, selectionStart, selectionEnd) => ({
  start,
  end,
  text,
  selectionStart,
  selectionEnd,
});

/** Wrap (or unwrap) the selection in an inline marker such as `**`. */
const toggleInline = (value, start, end, action) => {
  const marker = INLINE_MARKERS[action];
  const m = marker.length;
  const selected = value.slice(start, end);

  // Already wrapped just outside the selection → unwrap.
  const before = value.slice(start - m, start);
  const after = value.slice(end, end + m);
  const boldAroundItalic = action === 'italic' && value.slice(start - 2, start) === '**' && value.slice(start - 3, start) !== '***';
  if (before === marker && after === marker && !boldAroundItalic) {
    return edit(start - m, end + m, selected, start - m, end - m);
  }
  // Selection includes the markers → unwrap.
  if (selected.length >= 2 * m && selected.startsWith(marker) && selected.endsWith(marker)) {
    const inner = selected.slice(m, -m);
    return edit(start, end, inner, start, start + inner.length);
  }

  const inner = selected || INLINE_PLACEHOLDERS[action];
  return edit(start, end, `${marker}${inner}${marker}`, start + m, start + m + inner.length);
};

/** `[label](https://)` with the URL selected, ready to paste over. */
const insertLink = (value, start, end) => {
  const label = value.slice(start, end) || 'link text';
  const url = 'https://';
  const text = `[${label}](${url})`;
  const urlStart = start + label.length + 3;
  return edit(start, end, text, urlStart, urlStart + url.length);
};

/** Expand a selection to whole lines. */
const lineRange = (value, start, end) => {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  // A selection ending right after a newline doesn't include the next line.
  const effectiveEnd = end > start && value[end - 1] === '\n' ? end - 1 : end;
  const nextBreak = value.indexOf('\n', effectiveEnd);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  return [lineStart, lineEnd];
};

/** Add (or, when every line already has it, remove) a line prefix. */
const toggleLinePrefix = (value, start, end, action) => {
  const [lineStart, lineEnd] = lineRange(value, start, end);
  const lines = value.slice(lineStart, lineEnd).split('\n');
  const pattern = LINE_PATTERNS[action];
  const content = lines.filter((line) => line.trim());
  const allHave = content.length > 0 && content.every((line) => pattern.test(line));

  let counter = 0;
  const next = lines.map((line) => {
    if (!line.trim()) return line;
    if (allHave) return line.replace(pattern, '');
    let bare = line;
    if (action === 'heading' || action === 'subheading') bare = bare.replace(HEADING_PREFIX, '');
    if (action === 'bullets' || action === 'numbers') {
      bare = bare.replace(LINE_PATTERNS.bullets, '').replace(LINE_PATTERNS.numbers, '');
    }
    if (action === 'numbers') {
      counter += 1;
      return `${counter}. ${bare}`;
    }
    return `${LINE_PREFIXES[action]}${bare}`;
  });

  const text = next.join('\n');
  // An empty line gets the prefix so there's somewhere to type.
  if (!content.length) {
    const prefix = action === 'numbers' ? '1. ' : LINE_PREFIXES[action];
    return edit(lineStart, lineEnd, prefix, lineStart + prefix.length, lineStart + prefix.length);
  }
  return edit(lineStart, lineEnd, text, lineStart, lineStart + text.length);
};

/** A `[Counsel note: …]` paragraph of its own after the current line. */
const insertCounselNote = (value, start, end) => {
  const [, lineEnd] = lineRange(value, start, end);
  const selected = value.slice(start, end).trim();
  const body = selected && !selected.includes('\n') ? selected : 'question for the reviewing attorney';
  const note = `[Counsel note: ${body}]`;

  const before = value.slice(0, lineEnd);
  const after = value.slice(lineEnd);
  const leading = before.length === 0 ? '' : before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
  const trailing = after.length === 0 || after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n';
  const text = `${leading}${note}${trailing}`;
  const bodyStart = lineEnd + leading.length + '[Counsel note: '.length;
  return edit(lineEnd, lineEnd, text, bodyStart, bodyStart + body.length);
};

/**
 * Compute the edit for a toolbar action.
 *
 * @param {string} value - Current Markdown.
 * @param {number} start - Selection start.
 * @param {number} end - Selection end.
 * @param {keyof FORMAT_ACTIONS} action
 * @returns {{start: number, end: number, text: string, selectionStart: number, selectionEnd: number}}
 */
export const formatMarkdown = (value, start, end, action) => {
  const text = String(value ?? '');
  const a = Math.max(0, Math.min(start, end, text.length));
  const b = Math.max(0, Math.min(Math.max(start, end), text.length));
  switch (action) {
    case 'bold':
    case 'italic':
      return toggleInline(text, a, b, action);
    case 'link':
      return insertLink(text, a, b);
    case 'heading':
    case 'subheading':
    case 'bullets':
    case 'numbers':
    case 'callout':
      return toggleLinePrefix(text, a, b, action);
    case 'counselNote':
      return insertCounselNote(text, a, b);
    default:
      throw new Error(`Unknown format action: ${action}`);
  }
};

/** Apply an edit to a string (for tests and the no-execCommand fallback). */
export const applyEdit = (value, { start, end, text }) => `${value.slice(0, start)}${text}${value.slice(end)}`;

/**
 * Apply an edit to a textarea through the browser's editing stack so Ctrl+Z
 * undoes it; falls back to returning the new value when execCommand isn't
 * available (e.g. jsdom), in which case the caller sets it as state.
 *
 * @param {HTMLTextAreaElement} textarea
 * @param {object} change - Output of formatMarkdown.
 * @returns {string|null} The new value when the caller must set it, else null.
 */
export const applyEditToTextarea = (textarea, change) => {
  textarea.focus();
  textarea.setSelectionRange(change.start, change.end);
  let applied = false;
  try {
    applied = typeof document.execCommand === 'function'
      && document.execCommand('insertText', false, change.text);
  } catch {
    applied = false;
  }
  if (applied) {
    textarea.setSelectionRange(change.selectionStart, change.selectionEnd);
    return null;
  }
  return applyEdit(textarea.value, change);
};
